import { eproviderDisplayed, eproviderClicked, eproviderCallback } from '../models/trackingModels';
/**
 * @summary gets the tracking variant of the flow. This is used to alter the tracked URL
 * @param products the list of products in the order
 */
export function getTrackingPrefix(products) {
    if (products.length > 1) return 'familiebestilling-';
    if (products.length === 1 && products[0].identifier.variant === 'U30') return 'u30-';
    return '';
}

/**
 * @summary extracts the current tabs name and pushes to both GA and GA - ecommerce
 * @param {Array} tabsArray the list of tabs defined in the configuration
 * @param {Number} tabIndex the index of the current page
 * @param {String} subUrl optional parameter for additional url information e.g. person-1
 * @param {String} trackingPrefix the tracking variant of the flow
 * @param {Array} products passed on for checkout-subscription tracking
 */
export function trackVirtualPageView(tabsArray, tabIndex, subUrl, trackingPrefix, flowType, trackingType, products) {
    if (!tabsArray) return;
    const tab = tabsArray.find(tab => tab.index === tabIndex);

    if (!tab || tabsArray.length <= 0) return;

    if (subUrl) pushVirtualPageView(`${trackingPrefix}${tab.trackingUrl}/${subUrl}`, flowType, trackingType);
    else {
        // Documentaion specifies to not send ecommerce page view on the last page
        if (tabIndex < tabsArray.length - 1) pushVirtualEcommercePageView(tabIndex + 1, products, flowType);

        pushVirtualPageView(`${trackingPrefix}${tab.trackingUrl}`, flowType, trackingType);
    }
}

/**
 * @summary Pushes the defined object to GTM
 * @param {object} object The predefined object that GA can track
 */
function pushToGTM(object) {
    window.dataLayer?.push(object);
}

/**
 * @summary Pushes the fragmentet url to GA
 * @param {string} urlFragment The fragment that should be added to the root url
 */
function pushVirtualPageView(urlFragment, flowType, orderType = 'No type set for this flow') {
    const object = {
        event: 'virtual-pageview',
        'page-url': `/${urlFragment}`,
        'page-title': 'TBD',
        'order-flow': flowType, // TMW-1947: This property has to be named 'order-flow'
        'order-type': orderType, // TMW-2023: This property has to be named 'order-type'
    };

    pushToGTM(object);
}

/**
 * @summary Pushes changes of discount to datalayer
 */
export function trackU30Change() {
    const obj = { event: 'checkoutPopup', popupType: 'u30-eligible' };
    pushToGTM(obj);
}

/**
 * @summary Pushes the data of the added product to the datalayer
 * @param {object} product contains the product information of the product added
 */
export function trackProductAdded(product, flowType) {
    const category = productAndFlowToCategory(product,flowType);

    const name = (!product.identifier.variant) ? product.text.title : `${product.identifier.variant} ${product.text.title}`;

    const obj = {
        event: 'addToCart',
        ecommerce: {
            currencyCode: 'NOK',
            add: {
                products: [{
                    name,
                    id: product.identifier.id,
                    price: (!product.price.amount) ? '0' : product.price.amount,
                    category,
                    quantity: 1,
                }],
            },
        },
    };

    pushToGTM(obj);
}

/**
* @summary Pushes the data of the removed product to the datalayer
* @param {object} product contains the product information of the product to be removed
*/
export function trackProductRemoved(product, flowType) {
    const category = productAndFlowToCategory(product, flowType);

    const name = (!product.identifier.variant) ? product.text.title : `${product.identifier.variant} ${product.text.title}`;

    const obj = {
        event: 'removeFromCart',
        ecommerce: {
            remove: {
                products: [{
                    name,
                    id: product.identifier.id,
                    price: '',
                    category,
                    quantity: 1,
                }],
            },
        },
    };

    pushToGTM(obj);
}

/**
* @summary Pushes the 'purchase-complete' transaction for addons to GTM
* @param {string} transactionId Transaction Id. Required for purchases and refunds.
* @param {number} totalRevenue Total transaction value (incl. tax and shipping)
* @param {number} totaltax  Total tax for the transaction
* @param {array} addonsArray An array of the products containing: Name, Id and Price
*/
export function trackAddons(transactionId, totalRevenue, totaltax, addonsArray, flowType) {
    const products = [];

    addonsArray.forEach(addon => {
        products.push({
            name: addon.name,
            id: addon.id,
            price: addon.price,
            category: 'Addons',
            quantity: 1,
        });
    });

    const obj = {
        event: 'purchase-complete',
        ecommerce: {
            purchase: {
                actionField: {
                    id: transactionId, // Transaction ID. Required for purchases and refunds.
                    revenue: totalRevenue, // Total transaction value (incl. tax and shipping)
                    tax: totaltax,
                },
                products: products,
            },
        },
        'order-flow': flowType, // TMW-1947: This property has to be named 'order-flow'
    };

    pushToGTM(obj);
}

/**
* @param {Object} transaction The transaction object filled with data from endpoint.
*/
export function trackPurchaseComplete(transaction) {
    // This is not important when we get the correct object from endpoint.
    pushToGTM(transaction);
}

/**
 * @param {number} stepIndex The index of the current page.
 * @summary Pushes the virtual page view to the ecommerce section of GA
 */
function pushVirtualEcommercePageView(stepIndex, products, flowType) {

    const trackingProducts = [];

    products.forEach(p => {
        const name = (!p.identifier.variant) ? p.text.title : `${p.identifier.variant} ${p.text.title}`;

        trackingProducts.push({
            name,
            id: p.identifier.id,
            price: (!p.price.amount) ? '0' : p.price.amount,
            category: productAndFlowToCategory(p,flowType),
            quantity: 1,
        });
    });

    const object = {
        event: 'checkout-subscription',
        ecommerce: {
            checkout: {
                actionField: { step: stepIndex },
            },
            products: trackingProducts,
        },
    };

    pushToGTM(object);
}
/**
 * Returns a category for tracking based on product and flowtype
 * @param {object} product
 * @param {string} flowType
 * @returns {string} category
 */
function productAndFlowToCategory(product, flowType) {
    let category;

    switch (flowType) {
        case 'solo':
        case 'single':
        case 'multi':
            category = (!product.identifier.variant) ? 'Abonnement/Private/Fritt' : 'Abonnement/Private/U30';
            break;

        case 'share':
            category = 'Abonnement/Private/Family';
            break;

        case 'mbb':
            category = 'Abonnement/Private/Mbb';
            break;

        default:
            category = 'Abonnement/Private';
            break;
    }
    return category;
}

export function trackEprovider(eprovider, status) {
    const trackingObj = {
        event: 'EproviderEvent',
        providerName: eprovider,
        status: status,
    };
    pushToGTM(trackingObj);
}

export function trackEproviderDisplayed(eproviderList) {
    pushToGTM(eproviderDisplayed(eproviderList));
}

export function trackEproviderClicked(providerName) {
    pushToGTM(eproviderClicked(providerName));
}

export function trackEproviderCallback(providerName, status) {
    pushToGTM(eproviderCallback(providerName, status));
}